import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { listAssignmentScopes, updateAssignmentScope, type RoleAssignment } from "@/api/authorization";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/Modal";
import { useEffectivePermissions } from "../hooks/useEffectivePermissions";

export function AssignmentScopeDialog({ churchId, assignment, onClose }: {
  churchId: string;
  assignment: RoleAssignment;
  onClose: () => void;
}) {
  const { permissions } = useEffectivePermissions();
  const canManage = permissions.has("ROLES_MANAGE");
  const client = useQueryClient();
  const [ministryId, setMinistryId] = useState(assignment.ministry_id ?? "");
  const [confirmed, setConfirmed] = useState(false);
  const scopes = useQuery({
    queryKey: ["assignment-scopes", churchId],
    queryFn: () => listAssignmentScopes(churchId),
    enabled: canManage,
  });
  const mutation = useMutation({
    mutationFn: () => updateAssignmentScope(churchId, assignment.id, ministryId || null, assignment.ministry_id),
    onSuccess: async () => {
      // Remove cached data after an authorization change, including self-edits.
      await client.cancelQueries();
      await client.resetQueries();
      onClose();
    },
  });
  const changed = ministryId !== (assignment.ministry_id ?? "");
  const selected = scopes.data?.find(scope => scope.id === ministryId);
  const valid = ministryId === "" || selected?.is_active === true;
  const error = mutation.error as { response?: { data?: { detail?: string } } } | null;
  if (!canManage) return null;
  return (
    <Modal open onClose={() => { if (!mutation.isPending) onClose(); }} ariaLabelledBy="assignment-scope-title" panelClassName="max-w-xl">
      <h2 id="assignment-scope-title" className="text-xl font-semibold">Edit assignment scope</h2>
      <p className="mt-3">{assignment.user_name || assignment.user_email} · {assignment.role_name}</p>
      <p className="mt-2 text-sm text-slate-400">Current scope: {assignment.ministry_name ?? "Entire church"}. Assignment: {assignment.is_active ? "Active" : "Inactive"}.</p>
      <p className="mt-3 text-sm text-slate-300">Entire church applies this role’s permissions to Main Church and its ministries. A ministry scope applies them only to that ministry. Other role assignments remain in effect.</p>
      {scopes.isPending ? <p className="mt-4">Loading scopes...</p> : scopes.isError ? (
        <div role="alert" className="mt-4"><p>Unable to load assignment scopes.</p><Button onClick={() => void scopes.refetch()}>Try again</Button></div>
      ) : (
        <label className="mt-5 block text-sm">New scope
          <select aria-label="New scope" disabled={mutation.isPending} value={ministryId} onChange={event => { setMinistryId(event.target.value); setConfirmed(false); mutation.reset(); }} className="mt-2 w-full rounded-xl border border-slate-700 bg-slate-950 p-3">
            <option value="">Entire church</option>
            {scopes.data?.map(scope => <option key={scope.id} value={scope.id} disabled={!scope.is_active}>{scope.name}{scope.is_active ? "" : " (archived)"}</option>)}
          </select>
        </label>
      )}
      {changed && <label className="mt-5 flex items-start gap-3 text-sm">
        <input type="checkbox" checked={confirmed} disabled={mutation.isPending} onChange={event => setConfirmed(event.target.checked)} />
        <span>I confirm changing this role’s scope from {assignment.ministry_name ?? "Entire church"} to {ministryId ? selected?.name ?? "the selected ministry" : "Entire church"}. {assignment.is_active ? "This changes the user’s access." : "This assignment will remain inactive."}</span>
      </label>}
      {mutation.isError && <p role="alert" className="mt-4 text-rose-300">{error?.response?.data?.detail ?? "Unable to change scope. Try again."}</p>}
      <div className="mt-6 flex justify-end gap-3">
        <Button variant="secondary" disabled={mutation.isPending} onClick={onClose}>Cancel</Button>
        <Button disabled={!changed || !confirmed || !valid || !scopes.isSuccess || mutation.isPending} onClick={() => mutation.mutate()}>{mutation.isPending ? "Saving..." : "Save scope"}</Button>
      </div>
    </Modal>
  );
}
